import React from 'react';
import { Handle, Position } from '@xyflow/react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiFlag, FiHelpCircle, FiCheckCircle, FiXCircle, FiType } = FiIcons;

const CustomNode = ({ data, selected }) => {
  const getNodeIcon = () => {
    switch (data.type) {
      case 'start':
        return FiPlay;
      case 'end':
        return data.isSuccess ? FiCheckCircle : FiXCircle;
      case 'multiple-choice':
      case 'true-false':
        return FiHelpCircle;
      case 'short-answer':
        return FiType;
      default:
        return FiFlag;
    }
  };

  const getNodeColor = () => {
    switch (data.type) {
      case 'start':
        return 'bg-green-500 text-white';
      case 'end':
        return data.isSuccess 
          ? 'bg-green-500 text-white' 
          : 'bg-red-500 text-white';
      case 'multiple-choice':
      case 'true-false':
        return 'text-white';
      case 'short-answer':
        return 'bg-purple-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const isComplete = () => {
    if (data.type === 'multiple-choice') {
      return data.questionText && 
             data.choices?.length >= 2 && 
             data.choices.some(choice => choice.text && choice.isCorrect);
    }
    if (data.type === 'true-false') {
      return data.questionText;
    }
    if (data.type === 'short-answer') {
      return data.questionText && 
             data.correctAnswers?.length >= 1 && 
             data.correctAnswers.some(answer => answer.trim()) &&
             data.correctNextNodeId &&
             data.incorrectNextNodeId;
    }
    return true;
  };

  const getNodeTypeLabel = () => {
    switch (data.type) {
      case 'short-answer':
        return 'Kurzantwort';
      case 'multiple-choice':
        return 'Multiple Choice';
      case 'true-false':
        return 'Richtig/Falsch';
      default:
        return data.type;
    }
  };

  return (
    <div className={`
      relative px-4 py-2 rounded-lg border-2 min-w-[150px] max-w-[200px]
      ${selected ? 'border-blue-400' : 'border-gray-300'}
      ${isComplete() ? 'bg-white' : 'bg-red-50 border-red-300'}
      shadow-sm hover:shadow-md transition-shadow
    `}>
      {/* Input Handle */}
      {data.type !== 'start' && (
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3"
        />
      )}

      {/* Node Content */}
      <div className="flex items-center space-x-2">
        <div 
          className={`p-1 rounded ${getNodeColor()}`}
          style={
            data.type === 'multiple-choice' || data.type === 'true-false' 
              ? { backgroundColor: '#c8d8e4' } 
              : {}
          }
        >
          <SafeIcon icon={getNodeIcon()} className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate font-rubik" style={{ color: '#2b6777' }}>
            {data.label}
          </div>
          {data.questionText && (
            <div className="text-xs truncate font-rubik" style={{ color: '#6b7280' }}>
              {data.questionText}
            </div>
          )}
          {data.type && data.type !== 'start' && data.type !== 'end' && (
            <div className="text-xs font-medium text-blue-600 font-rubik">
              {getNodeTypeLabel()}
            </div>
          )}
        </div>
      </div>

      {/* Validation indicator */}
      {!isComplete() && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
      )}

      {/* Output Handle */}
      {data.type !== 'end' && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="w-3 h-3"
        />
      )}
    </div>
  );
};

export default CustomNode;